// src/hooks/layout/useAppLayout.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectNotificationItems,
  selectUnreadNotificationCount,
  selectUser,
} from '@/store/selectors/user';
import { selectModal } from '@/store/selectors/ui';
import { selectCatalogFilters } from '@/store/selectors/sixCatalog';
import { selectSixDetailActiveFilters } from '@/store/selectors/sixDetail';
import { openModal, closeModal } from '@/store/slices/ui/uiSlice';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { toggleFilterAndFetchThunk } from '@/store/thunks/sixCatalog/toggleFilterAndFetchThunk';
import {
  showCreateFolderModal,
  showCreateSixModal,
  showFileUploadModal,
} from '@/store/thunks/ui/uiThunk';
import { toggleSixDetailFilterThunk } from '@/store/thunks/sixDetail/applySixDetailFilterThunk';
import { fetchNotificationsThunk } from '@/store/thunks/user/fetchNotificationsThunk';
import { markNotificationReadThunk } from '@/store/thunks/user/markNotificationReadThunk';
import { logoutThunk } from '@/store/thunks/auth/logoutThunk';
import { getSidebarDefinition } from '@/layout/AppLayout/appLayoutSidebarConfig';
import { selectCanCreateSix } from '@/store/selectors/permissions';
import type { SidebarActionDefinition } from '@/types/AppLayout.types';
import {
  createHeaderNotification,
  resolveOrganizationName,
  resolveUserName,
  resolveUserEmail,
  buildUserProfile,
} from '@/utils/appLayout.utils';
import type { HeaderNotification, UserProfile } from '@/utils/appLayout.utils';

const DESKTOP_SIZE_BREAKPOINT = 1024;

interface UseAppLayoutState {
  isLoaderVisible: boolean;
  organizationName: string;
  userName: string;
  userEmail: string | undefined;
  avatarSrc: string | undefined;
  profileUser: UserProfile;
  headerNotifications: HeaderNotification[];
  unreadCount: number;
  profileDialogIsOpen: boolean;
  sidebarIsCollapsed: boolean;
  mobileMenuIsOpen: boolean;
  sidebarConfig: ReturnType<typeof getSidebarDefinition>;
}

interface UseAppLayoutHandlers {
  handleSidebarToggle: () => void;
  handleMobileOverlayClick: () => void;
  handleSidebarAction: (action: SidebarActionDefinition) => void;
  handleHelpClick: () => void;
  handleSelectProfile: () => void;
  handleCloseProfileDialog: () => void;
  handleSelectLogout: () => void;
  handleSaveProfile: (nextPhone: string) => Promise<void>;
  handleVerifyPhone: (code: string) => Promise<void>;
  handleResendCode: () => Promise<void>;
}

interface UseAppLayoutReturn {
  state: UseAppLayoutState;
  handlers: UseAppLayoutHandlers;
}

export function useAppLayout(): UseAppLayoutReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPathname = routerState.location.pathname;

  const {
    state: { show: isLoaderVisible },
  } = useGlobalLoader();
  const currentUser = useAppSelector(selectUser);
  const notificationItems = useAppSelector(selectNotificationItems);
  const unreadCount = useAppSelector(selectUnreadNotificationCount);
  const activeModal = useAppSelector(selectModal);
  const activeFilters = useAppSelector(selectCatalogFilters);
  const activeSixDetailFilters = useAppSelector(selectSixDetailActiveFilters);

  const [sidebarIsCollapsed, setSidebarIsCollapsed] = useState(false);
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);

  const canCreateSix = useAppSelector(selectCanCreateSix);

  // Fetch notifications on mount
  useEffect(() => {
    void dispatch(fetchNotificationsThunk());
  }, [dispatch]);

  const organizationName = useMemo(
    () => resolveOrganizationName(currentUser),
    [currentUser],
  );
  const userName = useMemo(() => resolveUserName(currentUser), [currentUser]);
  const userEmail = useMemo(() => resolveUserEmail(currentUser), [currentUser]);
  const avatarSrc = undefined as string | undefined; // No avatar field on User

  const profileUser = useMemo<UserProfile>(
    () => buildUserProfile(currentUser),
    [currentUser],
  );

  const handleMarkNotificationRead = useCallback(
    (notificationId: string) => {
      void dispatch(markNotificationReadThunk({
    notificationId,
    status: 'Read'
  }));
    },
    [dispatch],
  );

  const headerNotifications = useMemo<HeaderNotification[]>(
    () =>
      notificationItems.map((item) =>
        createHeaderNotification(item, handleMarkNotificationRead),
      ),
    [notificationItems, handleMarkNotificationRead],
  );

  const safeActiveFilters = useMemo(
    () => (Array.isArray(activeFilters) ? activeFilters : []),
    [activeFilters],
  );

  const safeActiveSixDetailFilters = useMemo(
    () => (Array.isArray(activeSixDetailFilters) ? activeSixDetailFilters : []),
    [activeSixDetailFilters],
  );

  const sidebarConfig = useMemo(
    () =>
      getSidebarDefinition({
        pathname: currentPathname,
        activeFilters: safeActiveFilters,
        activeSixDetailFilters: safeActiveSixDetailFilters,
        canCreateSix,
      }),
    [currentPathname, safeActiveFilters, safeActiveSixDetailFilters, canCreateSix],
  );

  const profileDialogIsOpen = activeModal === 'profile';

  const sidebarActionHandlers = useMemo<
    Partial<Record<string, (d: typeof dispatch) => void>>
  >(
    () => ({
      showCreateSixModal: (d) => {
        d(showCreateSixModal());
      },
      'catalog.toggleFilter.mySixes': (d) => {
        void d(toggleFilterAndFetchThunk('mySixes'));
      },
      'catalog.toggleFilter.recent': (d) => {
        void d(toggleFilterAndFetchThunk('recent'));
      },
      'catalog.toggleFilter.favorites': (d) => {
        void d(toggleFilterAndFetchThunk('favorites'));
      },
      'detail.openUploadDialog': (d) => {
        d(showFileUploadModal());
      },
      'detail.openCreateFolderDialog': (d) => {
        d(showCreateFolderModal());
      },
      'detail.toggleFilter.myFilesAndFolders': (d) => {
        void d(toggleSixDetailFilterThunk('myFilesAndFolders'));
      },
      'detail.toggleFilter.recent': (d) => {
        void d(toggleSixDetailFilterThunk('recent'));
      },
      'detail.toggleFilter.favorites': (d) => {
        void d(toggleSixDetailFilterThunk('favorites'));
      },
    }),
    [],
  );

  const handleSidebarToggle = useCallback((): void => {
    if (window.innerWidth < DESKTOP_SIZE_BREAKPOINT) {
      setMobileMenuIsOpen((previousState) => !previousState);
      return;
    }
    setSidebarIsCollapsed((previousState) => !previousState);
  }, []);

  const handleMobileOverlayClick = useCallback((): void => {
    setMobileMenuIsOpen(false);
  }, []);

  const handleSidebarAction = useCallback(
    (action: SidebarActionDefinition): void => {
      if (action.type === 'navigate' && action.pathname !== undefined) {
        void navigate({ to: action.pathname });
        if (window.innerWidth < DESKTOP_SIZE_BREAKPOINT) setMobileMenuIsOpen(false);
        return;
      }

      if (action.type !== 'dispatch' || action.actionKey === undefined) return;

      const handler = sidebarActionHandlers[action.actionKey];
      if (handler === undefined) return; // H-19: null check prevents crash
      handler(dispatch);

      if (window.innerWidth < DESKTOP_SIZE_BREAKPOINT) setMobileMenuIsOpen(false);
    },
    [dispatch, navigate, sidebarActionHandlers],
  );

  const handleHelpClick = useCallback((): void => {
    window.open('https://support.six3ro.com', '_blank', 'noopener,noreferrer');
  }, []);

  const handleSelectProfile = useCallback((): void => {
    dispatch(openModal({ modal: 'profile' }));
  }, [dispatch]);

  const handleCloseProfileDialog = useCallback((): void => {
    dispatch(closeModal());
  }, [dispatch]);

  const handleSelectLogout = useCallback((): void => {
    void dispatch(logoutThunk());
    void navigate({ to: '/login', replace: true });
  }, [dispatch, navigate]);

  const handleSaveProfile = useCallback(
    async(_nextPhone: string): Promise<void> => {
      await Promise.resolve();
    },
    [],
  );

  const handleVerifyPhone = useCallback(
    async(_code: string): Promise<void> => {
      await Promise.resolve();
    },
    [],
  );

  const handleResendCode = useCallback(async(): Promise<void> => {
    await Promise.resolve();
  }, []);

  return {
    state: {
      isLoaderVisible,
      organizationName,
      userName,
      userEmail,
      avatarSrc,
      profileUser,
      headerNotifications,
      unreadCount,
      profileDialogIsOpen,
      sidebarIsCollapsed,
      mobileMenuIsOpen,
      sidebarConfig,
    },
    handlers: {
      handleSidebarToggle,
      handleMobileOverlayClick,
      handleSidebarAction,
      handleHelpClick,
      handleSelectProfile,
      handleCloseProfileDialog,
      handleSelectLogout,
      handleSaveProfile,
      handleVerifyPhone,
      handleResendCode,
    },
  };
}
